import { createFileRoute, Link } from "@tanstack/react-router";
import { useQueries, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  AccountLimits,
  AuditEntry,
  Billing,
  Member,
  Organisation,
  Project,
  Rental,
  Service,
} from "@/lib/types";
import { canReadBilling } from "@/features/org/roles";
import { PLAN_LABEL } from "@/features/plan/plan";
import { humaniseAction, relativeTime } from "@/lib/format";
import { Head, Stack } from "@/ui/frame/Page";
import { Strip, Reading } from "@/ui/Strip";
import { Panel } from "@/ui/Panel";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { Button } from "@/ui/Button";
import { Chip } from "@/ui/Chip";
import { Lamp, serviceLabel, serviceLamp } from "@/ui/Lamp";
import { Notice } from "@/ui/Notice";
import { METER_NEAR } from "@/ui/Meter";
import { EmptyState, ErrorState, Skeleton, TableSkeleton } from "@/ui/States";

export const Route = createFileRoute("/_app/o/$orgId/")({
  component: OrganisationOverview,
});

/**
 * The organisation overview.
 *
 * # THE THREE QUESTIONS, AND WHERE THEY NOW LIVE
 *
 * What is it costing me, what have I got, and did somebody change something.
 * That part was settled two rebuilds ago and has not changed.
 *
 * What changed is where the answers sit. The previous version put the figures
 * and the warnings in a fixed 320px column down the right — correct in that
 * they must not scroll away, wrong in that it cost a fifth of the window on
 * every screen and left the services, which are the page, in the remainder.
 *
 * Here the figures are a card row across the top: five readings on one line,
 * which the services then scroll under. The services get the whole window.
 * That is the same insight the spine had, at a fraction of the cost.
 *
 * # THE ALLOWANCES STILL ONLY APPEAR AS AN EXCEPTION
 *
 * Six meters used to sit here permanently, and every one of them is also on
 * the billing screen — the same figures, drawn twice, two clicks apart. A
 * front page that repeats another screen is one people learn to skip. So
 * nothing is said until something is close to stopping working.
 */
function OrganisationOverview() {
  const { orgId } = Route.useParams();

  const organisation = useQuery({
    queryKey: ["organisation", orgId],
    queryFn: ({ signal }) =>
      api.get<{ organisation: Organisation }>(`/organisations/${orgId}`, undefined, signal),
  });

  const org = organisation.data?.organisation;
  const mayReadBilling = canReadBilling(org?.role);

  const members = useQuery({
    queryKey: ["members", orgId],
    queryFn: ({ signal }) =>
      api.get<{ members: Member[] }>(`/organisations/${orgId}/members`, undefined, signal),
  });

  const projects = useQuery({
    queryKey: ["projects", orgId],
    queryFn: ({ signal }) =>
      api.get<{ projects: Project[] }>(`/organisations/${orgId}/projects`, undefined, signal),
  });

  // The endpoint wraps it: { account: … }. Decoding it as the bare shape read
  // undefined.usage and took the whole page down with it, which is what the
  // typed call is for and why the type was wrong rather than absent.
  const limits = useQuery({
    queryKey: ["limits", orgId],
    queryFn: ({ signal }) =>
      api.get<{ account: AccountLimits }>(`/organisations/${orgId}/limits`, undefined, signal),
  });

  // Only for somebody allowed to see it. A developer with no billing
  // permission gets the rest of the page rather than an error where a figure
  // goes.
  const billing = useQuery({
    queryKey: ["billing", orgId],
    queryFn: ({ signal }) => api.get<Billing>(`/organisations/${orgId}/billing`, undefined, signal),
    enabled: mayReadBilling,
  });

  // What an account has now is time. The figure here used to be "this month so
  // far", which was the right question while usage became an invoice.
  const rental = useQuery({
    queryKey: ["rental", orgId],
    queryFn: ({ signal }) =>
      api.get<Rental>(`/organisations/${orgId}/billing/rental`, undefined, signal),
    enabled: mayReadBilling,
  });

  // What just happened: the first place somebody looks when something is
  // different and they did not do it.
  const activity = useQuery({
    queryKey: ["audit", orgId, "recent"],
    queryFn: ({ signal }) =>
      api.get<{ entries: AuditEntry[] | null }>(
        `/organisations/${orgId}/audit-log`,
        { limit: 6 },
        signal,
      ),
  });

  const list = projects.data?.projects ?? [];

  // Six parallel requests, because that is the shape of the API: services are
  // listed under the project that owns them and there is no cross-project list
  // on the customer side. Capped at six, which is also what the page shows —
  // an account with forty would otherwise open forty connections to draw a
  // list that ends after the sixth.
  const shown = list.slice(0, 6);
  const serviceQueries = useQueries({
    queries: shown.map((project) => ({
      queryKey: ["services", orgId, project.id],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        api.get<{ services: Service[] }>(
          `/organisations/${orgId}/projects/${project.id}/services`,
          undefined,
          signal,
        ),
      // Live states on the front page, so they go stale fast.
      refetchInterval: 20000,
    })),
  });

  const recent = activity.data?.entries ?? [];
  const account = limits.data?.account;
  // 75% is where Meter turns amber and this has to agree with it: a warning
  // that fires at a different point than the bar it links to is two answers to
  // one question.
  const nearCeiling = account
    ? allowanceRows(account).filter((row) => row.ratio >= METER_NEAR)
    : [];
  const money = billing.data;
  const capacity = rental.data;

  return (
    <>
      <Head
        title={org?.display_name ?? <Skeleton className="h-7 w-56" />}
        description="What this account is running, and what it is costing."
        actions={
          <Link to="/o/$orgId/projects" params={{ orgId }}>
            <Button tone="primary">New project</Button>
          </Link>
        }
      />

      {/* The readings. Five, in the order the questions are asked: how long
          have I got, what is left to pay with, how much am I running, how
          close to the ceiling, and who else is here. */}
      <Strip>
        {mayReadBilling && (
          <Reading
            label="Rental ends in"
            lamp={
              capacity && (
                <Lamp
                  state={
                    !capacity.paid_through
                      ? "idle"
                      : !capacity.active
                        ? "down"
                        : capacity.days_left <= capacity.notice_days
                          ? "warn"
                          : "up"
                  }
                />
              )
            }
          >
            {!capacity ? (
              <Skeleton className="h-5 w-20" />
            ) : !capacity.paid_through ? (
              "Not started"
            ) : !capacity.active ? (
              "Ran out"
            ) : (
              `${capacity.days_left} ${capacity.days_left === 1 ? "day" : "days"}`
            )}
          </Reading>
        )}

        {mayReadBilling && (
          <Reading label="Credit">
            {capacity ? capacity.balance : <Skeleton className="h-5 w-20" />}
          </Reading>
        )}

        <Reading label="Services">
          {account ? (
            `${account.usage.services} of ${account.limits.services}`
          ) : (
            <Skeleton className="h-5 w-16" />
          )}
        </Reading>

        <Reading label="Processor">
          {account ? (
            `${(account.usage.total_cpu_millicores / 1000).toFixed(1)} of ${(
              account.limits.total_cpu_millicores / 1000
            ).toFixed(0)}`
          ) : (
            <Skeleton className="h-5 w-16" />
          )}
        </Reading>

        <Reading label="Members">
          {members.data ? String(members.data.members.length) : <Skeleton className="h-5 w-8" />}
        </Reading>

        {account && (
          <Reading label="Plan">{PLAN_LABEL[account.package]}</Reading>
        )}
      </Strip>

      <Stack>
        {/* Anything wrong, in words, before anything else. These are the states
            that stop an account working and all of them were once discoverable
            only by opening the billing screen and reading it. */}
        {capacity && !capacity.active && capacity.paid_through ? (
          <Notice
            
            title="Services are stopped because the rental ran out"
            actions={
              <Link to="/o/$orgId/billing" params={{ orgId }}>
                <Button size="dense">Extend the rental</Button>
              </Link>
            }
          >
            {`Extending the ${capacity.plan} rental starts them again where they left off. The services themselves are deleted ${capacity.delete_after_days} days after they stopped.`}
          </Notice>
        ) : capacity && !capacity.paid_through ? (
          <Notice
            title="Nothing is rented on this account"
            actions={
              <Link to="/o/$orgId/billing" params={{ orgId }}>
                <Button size="dense">Rent a plan</Button>
              </Link>
            }
          >
            Capacity is rented by the month and paid for in advance. Put credit on the account and
            rent a plan, and services can start straight away.
          </Notice>
        ) : capacity && capacity.days_left <= capacity.notice_days ? (
          <Notice
            title={`The rental ends in ${capacity.days_left} ${capacity.days_left === 1 ? "day" : "days"}`}
            actions={
              <Link to="/o/$orgId/billing" params={{ orgId }}>
                <Button size="dense">Extend the rental</Button>
              </Link>
            }
          >
            Extend it and nothing changes. Let it run out and the services stop on that date; your
            data is kept either way.
          </Notice>
        ) : money?.identity_missing ? (
          <Notice
            title="An invoice cannot be issued yet"
            actions={
              <Link to="/o/$orgId/billing" params={{ orgId }}>
                <Button size="dense">Add the details</Button>
              </Link>
            }
          >
            {money.identity_missing}
          </Notice>
        ) : null}

        {nearCeiling.length > 0 && (
          <Notice
            title={
              nearCeiling.length === 1
                ? `${nearCeiling[0]!.label} is nearly at its limit`
                : `${nearCeiling.length} allowances are nearly at their limit`
            }
            actions={
              <Link to="/o/$orgId/billing" params={{ orgId }}>
                <Button size="dense">See the plan</Button>
              </Link>
            }
          >
            {nearCeiling.map((row) => `${row.label} ${row.display}`).join(" · ")}
          </Notice>
        )}

        {/* The services are the page.
            A project is a folder, and nobody is anxious about a folder at nine
            in the morning. The services are what the visit is about, so they
            are the content and the project is the heading they sit under. */}
        {projects.isLoading ? (
          <Panel padded={false}>
            <TableSkeleton rows={4} columns={3} />
          </Panel>
        ) : projects.isError ? (
          <ErrorState error={projects.error} onRetry={() => void projects.refetch()} />
        ) : list.length === 0 ? (
          <Panel title="Services" padded={false}>
            <EmptyState
              title="Nothing is running yet"
              description="A project holds the services, databases and domains of one application. Make one, and what you deploy into it appears here."
              action={
                <Link to="/o/$orgId/projects" params={{ orgId }}>
                  <Button tone="primary" size="dense">
                    New project
                  </Button>
                </Link>
              }
            />
          </Panel>
        ) : (
          <div className="flex flex-col gap-4">
            {shown.map((project, i) => (
              <ProjectServices
                key={project.id}
                orgId={orgId}
                project={project}
                query={serviceQueries[i]}
              />
            ))}
            {list.length > shown.length && (
              <Link
                to="/o/$orgId/projects"
                params={{ orgId }}
                className="t-meta px-1 underline-offset-4 hover:text-[var(--text-primary)] hover:underline"
              >
                {list.length - shown.length} more{" "}
                {list.length - shown.length === 1 ? "project" : "projects"} &rarr;
              </Link>
            )}
          </div>
        )}

        {/* The record, last and quiet. It answers "did somebody change
            something", which is the second question and never the first. */}
        <Panel
          title="Recent activity"
          padded={false}
          actions={
            <Link to="/o/$orgId/audit" params={{ orgId }}>
              <Button tone="ghost" size="dense">
                All activity
              </Button>
            </Link>
          }
        >
          {activity.isLoading ? (
            <TableSkeleton rows={4} columns={2} lamps={false} />
          ) : recent.length === 0 ? (
            <EmptyState title="Nothing has happened yet" />
          ) : (
            <ul>
              {recent.map((entry) => (
                <li
                  key={entry.id}
                  className="flex h-[var(--h-dense)] items-center gap-3 border-b border-[var(--border)] px-4 last:border-b-0"
                >
                  <span className="min-w-0 flex-1 truncate">
                    <span className="t-strong">{humaniseAction(entry.action)}</span>
                    {entry.target_label ? (
                      <span className="t-body ml-2 text-[var(--text-secondary)]">
                        {entry.target_label}
                      </span>
                    ) : null}
                  </span>
                  <span
                    className="mono shrink-0 text-[var(--text-muted)]"
                    title={new Date(entry.created_at).toLocaleString()}
                  >
                    {relativeTime(entry.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </Stack>
    </>
  );
}

/**
 * One project, and what it is running.
 *
 * The project is the card header — name, slug, what else is in it — and the
 * services are the rows. Each row leads with its lamp in the gutter, so six
 * projects stacked down the page put every service's state on one vertical
 * line regardless of which project it belongs to. That is the thing the
 * previous version could not do: it put a 136px state column inside each
 * card, so the column restarted at every heading.
 */
function ProjectServices({
  orgId,
  project,
  query,
}: {
  orgId: string;
  project: Project;
  query?: { data?: { services: Service[] }; isLoading: boolean };
}) {
  const services = query?.data?.services ?? [];
  const extras = [
    project.databases > 0 &&
      `${project.databases} ${project.databases === 1 ? "database" : "databases"}`,
    project.domains > 0 && `${project.domains} ${project.domains === 1 ? "domain" : "domains"}`,
  ].filter(Boolean) as string[];

  return (
    <Panel
      padded={false}
      title={
        <span className="flex min-w-0 items-center gap-2.5">
          <Link
            to="/o/$orgId/projects/$projectId"
            params={{ orgId, projectId: project.id }}
            className="min-w-0 truncate underline-offset-4 hover:underline"
          >
            {project.display_name}
          </Link>
          <Chip>{project.slug}</Chip>
        </span>
      }
      actions={extras.length > 0 ? <span className="t-meta">{extras.join(" · ")}</span> : undefined}
    >
      {query?.isLoading ? (
        <TableSkeleton rows={Math.min(project.services || 1, 3)} columns={3} />
      ) : services.length === 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4">
          <p className="t-body">This project has nothing in it yet.</p>
          <Link to="/o/$orgId/projects/$projectId" params={{ orgId, projectId: project.id }}>
            <Button size="dense">Add a service</Button>
          </Link>
        </div>
      ) : (
        // The house table, like every other list in the product. Each row
        // leads with its status dot in the same narrow first column, so six
        // projects stacked down the page put every service's state on one
        // vertical line regardless of which project it belongs to.
        <Table lamps fixed minWidth="46rem">
          <thead>
            <tr>
              <Th quiet>State</Th>
              <Th width="w-[22%]">Service</Th>
              <Th>Running</Th>
              <Th width="w-[12%]">State</Th>
              <Th width="w-[10%]" align="right">
                Size
              </Th>
              <Th width="w-[12%]" align="right">
                Changed
              </Th>
            </tr>
          </thead>
          <tbody>
            {services.map((service) => (
              <Tr key={service.id} live>
                <Td>
                  <span className="flex justify-center">
                    <Lamp state={serviceLamp(service.state)} />
                  </span>
                </Td>

                <Td clip>
                  <Link
                    to="/o/$orgId/projects/$projectId/services/$serviceId"
                    params={{ orgId, projectId: project.id, serviceId: service.id }}
                    className="block min-w-0"
                  >
                    <span className="t-strong block truncate hover:underline">
                      {service.display_name}
                    </span>
                    {/* Where it answers, for a service that has an address. A
                        worker has none and says its kind instead. */}
                    {service.url ? (
                      <span className="mono block truncate text-xs text-[var(--text-muted)]">
                        {service.url.replace(/^https?:\/\//, "")}
                      </span>
                    ) : (
                      <span className="t-meta block">{service.kind}</span>
                    )}
                  </Link>
                </Td>

                {/* What it is running. An image pinned by digest is 71
                    characters of unbreakable text and is the exact value that
                    collapsed every other column in this row once already, so
                    it is shown whole and the column cuts it. */}
                <Td clip className="mono text-xs" title={service.image ?? undefined}>
                  {service.source_type === "image"
                    ? service.image
                    : service.git
                      ? `${service.git.repository}#${service.git.branch}`
                      : ""}
                </Td>

                <Td clip>{serviceLabel(service.state)}</Td>

                <Td numeric>
                  {service.resources.replicas}&times; {service.resources.cpu_millicores}m
                </Td>

                <Td numeric title={new Date(service.state_changed_at).toLocaleString()}>
                  {relativeTime(service.state_changed_at)}
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}
    </Panel>
  );
}

/**
 * Every ceiling the account has, as rows a meter can render.
 *
 * `limits` returns seven of these and the page shows one. The rest are the
 * answer to "why was I refused", which somebody otherwise finds out by being
 * refused.
 */
function allowanceRows(account: AccountLimits) {
  const row = (label: string, value: number, limit: number, display: string) => ({
    label,
    value,
    limit,
    display,
    ratio: limit > 0 ? value / limit : 0,
  });
  const gib = (mib: number) => (mib / 1024).toFixed(1);
  return [
    row(
      "Services",
      account.usage.services,
      account.limits.services,
      `${account.usage.services} of ${account.limits.services}`,
    ),
    row(
      "Databases",
      account.usage.databases,
      account.limits.databases,
      `${account.usage.databases} of ${account.limits.databases}`,
    ),
    row(
      "Domains",
      account.usage.domains,
      account.limits.domains,
      `${account.usage.domains} of ${account.limits.domains}`,
    ),
    row(
      "Processor",
      account.usage.total_cpu_millicores,
      account.limits.total_cpu_millicores,
      `${(account.usage.total_cpu_millicores / 1000).toFixed(1)} of ${(
        account.limits.total_cpu_millicores / 1000
      ).toFixed(0)} cores`,
    ),
    row(
      "Memory",
      account.usage.total_memory_mib,
      account.limits.total_memory_mib,
      `${gib(account.usage.total_memory_mib)} of ${gib(account.limits.total_memory_mib)} GB`,
    ),
    row(
      "Storage",
      account.usage.total_storage_gib,
      account.limits.total_storage_gib,
      `${account.usage.total_storage_gib} of ${account.limits.total_storage_gib} GB`,
    ),
  ];
}
